"use client"

import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

export function IntegrationRequiredPlaceholder({
  icon: Icon,
  showIllustration = true,
  sectionIcon: SectionIcon,
  sectionIconTestId,
  sectionTitle,
  sectionDescription,
  iconTestId,
  title,
  description,
  supportingText,
  action,
}: {
  icon: LucideIcon
  showIllustration?: boolean
  sectionIcon?: LucideIcon
  sectionIconTestId?: string
  sectionTitle?: string
  sectionDescription?: string
  iconTestId: string
  title: string
  description: string
  supportingText: string
  action?: ReactNode
}) {
  return (
    <Card className="min-h-80">
      {sectionTitle ? (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {SectionIcon ? (
              <SectionIcon
                aria-hidden="true"
                className="h-5 w-5"
                data-testid={sectionIconTestId}
              />
            ) : null}
            {sectionTitle}
          </CardTitle>
          {sectionDescription ? <CardDescription>{sectionDescription}</CardDescription> : null}
        </CardHeader>
      ) : null}
      <CardContent className="flex min-h-56 items-center justify-center px-6 py-8">
        <div className="flex max-w-lg flex-col items-center text-center">
          {showIllustration ? (
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Icon
                aria-hidden="true"
                className="h-8 w-8 text-muted-foreground"
                data-testid={iconTestId}
              />
            </div>
          ) : null}
          <h2 className="text-balance text-xl font-semibold tracking-tight">
            {title}
          </h2>
          <p className="mt-2 text-pretty text-sm leading-6 text-muted-foreground">
            {description}
          </p>
          <p className="mt-2 text-pretty text-sm text-muted-foreground">
            {supportingText}
          </p>
          {action ? <div className="mt-4">{action}</div> : null}
        </div>
      </CardContent>
    </Card>
  )
}

export function IntegrationSectionLoadingPlaceholder({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon
  title: string
  description: string
}) {
  return (
    <Card className="min-h-80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon aria-hidden="true" className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-40 animate-pulse rounded-lg bg-muted" />
      </CardContent>
    </Card>
  )
}
